/**
 * signaling.ts — thin typed WebSocket client for the SPEC §4 signaling envelope.
 *
 * The console connects to `signal_url` (wss://HOST/api/v1/session/<id>/signal)
 * carrying the short-lived `signal_token`. The broker validates that the token's
 * session_id matches the URL and that the session belongs to the caller's org
 * (SPEC §4 / §11.1) — we never relay across a tenant boundary client-side either.
 *
 * Resilience: the socket reconnects on an unexpected close with exponential
 * backoff + jitter (architecture §: "reconnect w/ backoff+jitter"). A clean
 * close (we called close(), or the broker sent session_end) does NOT reconnect.
 * On reconnect we re-emit `hello`; ICE trickle resumes naturally and the viewer
 * triggers an ICE restart when it sees the socket come back (onReconnect).
 */

import type {
  ErrorPayload,
  IceCandidatePayload,
  SdpPayload,
  SignalEnvelope,
  SignalType,
} from '@/types';

type Handler = (env: SignalEnvelope) => void;

export interface SignalingOptions {
  url: string;
  token: string;
  sessionId: string;
  onOpen?: () => void;
  onClose?: (ev: CloseEvent) => void;
  onError?: (payload: ErrorPayload) => void;
  /** Fired after a *reconnect* (not the first connect) succeeds. */
  onReconnect?: () => void;
  /** Max reconnect attempts before giving up (default 8). */
  maxReconnect?: number;
}

const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15_000;

export class SignalingClient {
  private ws: WebSocket | null = null;
  private readonly handlers = new Map<SignalType, Set<Handler>>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private attempts = 0;
  private everOpened = false;

  constructor(private readonly opts: SignalingOptions) {}

  connect(): void {
    if (this.closed) return;
    // M6: carry the JWT in the Sec-WebSocket-Protocol header (offered as a paired
    // subprotocol) instead of the URL query, so it isn't captured in proxy access
    // logs / browser history. The server selects "helm-bearer" and reads the
    // paired token value.
    const ws = new WebSocket(this.opts.url, ['helm-bearer', this.opts.token]);
    this.ws = ws;

    ws.onopen = () => {
      const reconnected = this.everOpened;
      this.everOpened = true;
      this.attempts = 0;
      this.send('hello', {});
      this.startHeartbeat();
      this.opts.onOpen?.();
      if (reconnected) this.opts.onReconnect?.();
    };
    ws.onmessage = (ev) => this.dispatch(ev);
    ws.onclose = (ev) => {
      this.stopHeartbeat();
      this.ws = null;
      if (this.closed) return;
      this.opts.onClose?.(ev);
      // 1000 (normal) / 1001 (going away) after a session_end are terminal.
      if (ev.code === 1000) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      this.opts.onError?.({ code: 'ws_error', message: 'signaling socket error' });
    };
  }

  private scheduleReconnect(): void {
    const max = this.opts.maxReconnect ?? 8;
    if (this.attempts >= max) {
      this.opts.onError?.({ code: 'ws_gaveup', message: 'signaling reconnect failed' });
      return;
    }
    this.attempts += 1;
    // Exponential backoff with full jitter.
    const ceiling = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** (this.attempts - 1));
    const delay = Math.floor(Math.random() * ceiling);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private dispatch(ev: MessageEvent): void {
    let env: SignalEnvelope;
    try {
      env = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as SignalEnvelope;
    } catch {
      return;
    }
    if (env.type === 'error') {
      this.opts.onError?.(env.payload as ErrorPayload);
    }
    const set = this.handlers.get(env.type);
    if (set) for (const h of set) h(env);
  }

  on(type: SignalType, handler: Handler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    return () => set?.delete(handler);
  }

  send<P>(type: SignalType, payload: P): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    const env: SignalEnvelope<P> = {
      type,
      session_id: this.opts.sessionId,
      payload,
      ts: Date.now(),
    };
    this.ws.send(JSON.stringify(env));
  }

  sendOffer(_: SdpPayload): void {
    // The browser is the ANSWERER (SPEC §4 step 3: "Agent is the WebRTC offerer").
    // Offers are never sent from the console. This method exists only to make
    // that invariant explicit; calling it is a programming error.
    throw new Error('console is the answerer; it must not send sdp_offer');
  }

  sendAnswer(payload: SdpPayload): void {
    this.send('sdp_answer', payload);
  }

  sendIce(payload: IceCandidatePayload): void {
    this.send('ice_candidate', payload);
  }

  sendSessionEnd(): void {
    this.send('session_end', {});
  }

  /** True while the socket is open. */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => this.send('heartbeat', {}), 30_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.sendSessionEnd();
    } catch {
      /* socket may already be gone */
    }
    try {
      this.ws?.close(1000, 'client closed');
    } catch {
      /* ignore */
    }
    this.ws = null;
  }
}
